// Vertex Automation — content model (placeholder polished content)
import {
  Workflow, Brain, Bot, LineChart, ShieldCheck, Cpu,
  Building2, Landmark, Stethoscope, ShoppingBag, Truck, Factory,
} from "lucide-react";

export const NAV = [
  { label: "Services", href: "#services" },
  { label: "Solutions", href: "#solutions" },
  { label: "Process", href: "#process" },
  { label: "Work", href: "#work" },
];

export const STATS = [
  { value: "$2.4B+", label: "Operational value automated" },
  { value: "99.98%", label: "Autonomous uptime" },
  { value: "180+", label: "Enterprise deployments" },
  { value: "42M", label: "Decisions / day" },
];

export const MARQUEE = [
  "Autonomous Agents", "Predictive Ops", "Zero-Touch Workflows",
  "Vision Systems", "Real-Time Inference", "Self-Healing Pipelines",
  "Human-in-the-Loop", "Enterprise Grade",
];

export const SERVICES = [
  {
    icon: Bot,
    title: "Autonomous Agents",
    desc: "Goal-driven AI workers that plan, act and adapt across your entire tool stack without supervision.",
    span: "lg:col-span-7",
    tag: "01",
  },
  {
    icon: Brain,
    title: "Applied Intelligence",
    desc: "Custom models trained on your proprietary data, tuned for precision at scale.",
    span: "lg:col-span-5",
    tag: "02",
  },
  {
    icon: Workflow,
    title: "Process Orchestration",
    desc: "Compose thousands of tasks into resilient, observable pipelines.",
    span: "lg:col-span-5",
    tag: "03",
  },
  {
    icon: LineChart,
    title: "Predictive Analytics",
    desc: "Forecast demand, risk and failure before it happens — with confidence intervals you can act on.",
    span: "lg:col-span-7",
    tag: "04",
  },
  {
    icon: Cpu,
    title: "Edge Deployment",
    desc: "Sub-10ms inference at the edge, from factory floor to retail shelf.",
    span: "lg:col-span-6",
    tag: "05",
  },
  {
    icon: ShieldCheck,
    title: "Governance & Safety",
    desc: "SOC 2, audit trails and policy guardrails baked into every agent decision.",
    span: "lg:col-span-6",
    tag: "06",
  },
];

export const SOLUTIONS = [
  { icon: Building2, name: "Financial Services" },
  { icon: Stethoscope, name: "Healthcare" },
  { icon: Factory, name: "Manufacturing" },
  { icon: ShoppingBag, name: "Retail & CPG" },
  { icon: Truck, name: "Logistics" },
  { icon: Landmark, name: "Public Sector" },
];

export const PROCESS = [
  {
    n: "01",
    title: "Discover",
    body: "We map your operations, quantify friction and identify where autonomy compounds the most value.",
  },
  {
    n: "02",
    title: "Architect",
    body: "A bespoke intelligence blueprint — models, agents and guardrails designed around your data reality.",
  },
  {
    n: "03",
    title: "Deploy",
    body: "Ship to production in weeks, not quarters, with human-in-the-loop safeguards from day one.",
  },
  {
    n: "04",
    title: "Compound",
    body: "Systems that learn continuously, driving measurable gains that accelerate every quarter.",
  },
];

export const CASES = [
  {
    img: "https://images.unsplash.com/photo-1655393001768-d946c97d6fd1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxBSSUyMHJvYm90aWNzJTIwZnV0dXJpc3RpYyUyMHRlY2hub2xvZ3klMjBsaWdodHxlbnwwfHx8fDE3ODU0Mzk0OTh8MA&ixlib=rb-4.1.0&q=85",
    client: "Meridian Industrial",
    sector: "Advanced Manufacturing",
    metric: "37%",
    result: "reduction in unplanned downtime with predictive maintenance agents.",
  },
  {
    img: "https://images.unsplash.com/photo-1737644467636-6b0053476bb2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxBSSUyMHJvYm90aWNzJTIwZnV0dXJpc3RpYyUyMHRlY2hub2xvZ3klMjBsaWdodHxlbnwwfHx8fDE3ODU0Mzk0OTh8MA&ixlib=rb-4.1.0&q=85",
    client: "Northwind Logistics",
    sector: "Supply Chain",
    metric: "5.2x",
    result: "throughput on order routing after full workflow autonomy.",
  },
];

export const TESTIMONIALS = [
  {
    quote:
      "Vertex didn't sell us a model. They engineered an operating system for our business. Eighteen months in, autonomy touches every decision we make.",
    name: "Elena Vasquez",
    role: "COO, Meridian Industrial",
    img: "https://images.unsplash.com/photo-1604904612715-47bf9d9bc670?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0JTIwc3R1ZGlvfGVufDB8fHx8MTc4NTQzOTQ5OHww&ixlib=rb-4.1.0&q=85",
  },
  {
    quote:
      "The most technically serious AI partner we've worked with. Safety, observability and speed — they refused to trade one for another.",
    name: "Marcus Chen",
    role: "VP Engineering, Northwind",
    img: "https://images.unsplash.com/photo-1614786269829-d24616faf56d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0JTIwc3R1ZGlvfGVufDB8fHx8MTc4NTQzOTQ5OHww&ixlib=rb-4.1.0&q=85",
  },
];

export const FAQ = [
  {
    q: "How quickly can we see production value?",
    a: "Most engagements ship a first autonomous workflow to production within 6–8 weeks. We prioritise a high-leverage use case, prove the value, then compound from there.",
  },
  {
    q: "How do you handle data security and governance?",
    a: "Every deployment is SOC 2 aligned with full audit trails, role-based access, policy guardrails and human-in-the-loop checkpoints. Your data never trains models outside your environment.",
  },
  {
    q: "Do we need an in-house ML team?",
    a: "No. Vertex operates as your embedded intelligence team — we architect, deploy and maintain. We also upskill your engineers so ownership can transfer over time.",
  },
  {
    q: "Which models and infrastructure do you use?",
    a: "We are model-agnostic and infrastructure-agnostic. We select the right frontier or open-weight models per task and deploy to your cloud, on-prem or the edge.",
  },
  {
    q: "What does an engagement cost?",
    a: "Engagements are scoped to outcomes, not headcount. After a discovery sprint we present a fixed roadmap with clear value milestones. Book a strategy call to get an estimate.",
  },
];

export const CONTACT = {
  company: "Vertex Automation",
  email: "admin@vertexautomationcanada.com",
  phone: "(416) 726-9155",
  address: "30 Eglinton Ave W, Mississauga, ON L5R 3E7, Canada",
};
